<template>
  <div :class="$style.wrapper">
    <ModalManager @onClose="closeModal" />
    <main :class="$style.main">
      <div>
        <div :class="$style.mainHeader">
          <div :class="$style.sidebarOpen">
            <button type="button" :class="$style.sidebarToggle" @click.prevent="showSidebar">
              <span :class="$style.reader">
                {{ t('$hg.nav.toggleLabel') }}
              </span>
              <HgIcon icon="Menu" aria-hidden="true" />
            </button>
          </div>

          <div :class="$style.navigationWrapper">
            <!-- @slot Default slot to show items before the language switcher. -->
            <slot name="headerMetaNavigation" />

            <LanguageSwitcher id="header-language-switch" :currentLanguage="currentLanguage" />
          </div>
        </div>
      </div>
      <div :class="$style.content">
        <!-- @slot Default slot for the main content in the layout. -->
        <slot />
      </div>
    </main>

    <nav ref="sidebar" :class="[$style.sidebar, { [$style.isVisible]: !isSidebarHidden }]">
      <div :class="$style.sidebarSectionTop">
        <AgencySwitcher
          v-if="activeAgency"
          :agency="activeAgency"
          @agencySwitched="handleAgencySwitch"
        />
        <div :class="$style.sidebarClose">
          <button type="button" :class="$style.sidebarToggle" @click.prevent="hideSidebar">
            <span :class="$style.reader">
              {{ t('$hg.nav.closeMenu') }}
            </span>
            <HgIcon icon="Close" theme="light" aria-hidden="true" />
          </button>
        </div>
      </div>
      <!-- @slot Default slot for the sidebar main navigation. -->
      <div :class="$style.sidebarSectionMenu">
        <slot name="sidebarMainNavigation" />
        <div :class="$style.brandingWrapper">
          <div v-if="hasCCCGroup" :class="$style.cccButtons">
            <HgButton
              data-cy="ccc-agency-switcher-button"
              :class="$style['ccc-agency-switch-button']"
              @click="openModal(ModalIds.AgencySwitcherModal)"
              >{{ t('agency.cccAccess.cccAgencySwitcher') }}
            </HgButton>
            <HgButton
              v-if="isFlatfoxActive"
              icon="ExternalLink"
              data-cy="ccc-manage-in-flatfox-button"
              :class="$style['ccc-manage-in-flatfox-button']"
              @click="openCustomerListingsOnFlatfox"
              >{{ t('agency.cccAccess.manageInFlatfox') }}
            </HgButton>
          </div>
          <div :class="$style.contactBox">
            <div :class="$style.headingContainer">
              <h4>{{ t('adminDashboard.contactBox.header') }}</h4>
              <span v-if="supportContact.ownerName">
                {{ t('adminDashboard.contactBox.text', [supportContact.ownerName]) }}
              </span>
            </div>
            <div :class="$style.iconContactContainer">
              <HgIcon size="large" icon="Phone" :themeClass="$style.contactIcon" />
              <a :href="`tel:${supportContact.phone}`">{{ supportContact.phone }}</a>
            </div>
            <div :class="$style.iconContactContainer">
              <HgIcon size="large" icon="Email" :themeClass="$style.contactIcon" />
              <a :href="`mailto:${supportContact.email}`">{{ supportContact.email }}</a>
            </div>
            <div :class="$style.helpCenterContainer">
              <span>
                {{ t('adminDashboard.contactBox.helpCenterText') }}
                <HgLink
                  :href="t('adminDashboard.contactBox.helpCenterUrl')"
                  target="_blank"
                  @click="trackHelpCenterLinkClick"
                >
                  {{ t('adminDashboard.contactBox.helpCenterLink') }}
                </HgLink>
              </span>
            </div>
          </div>
          <SMGLogo :class="$style.SMGLogo" />
        </div>
      </div>

      <!-- @slot Default slot to show items before the language switcher. -->
      <slot name="sidebarMetaNavigation" />

      <div :class="$style.sidebarSectionLangs">
        <LanguageSwitcher
          id="side-menu-language-switch"
          :isFooter="true"
          :currentLanguage="currentLanguage"
        />
      </div>
    </nav>

    <div
      :class="[
        $style.backdrop,
        {
          [$style.isVisible]: !isSidebarHidden,
        },
      ]"
      @click="hideSidebar"
    />
  </div>
</template>

<script setup lang="ts">
import { formatSwissPhoneNumber } from '@/helpers/phoneNumberUtils';
import { HgButton, HgIcon, HgLink, HgSupportedLanguage } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { useAgencyStore } from '@/stores/agency/useAgencyStore';

import SMGLogo from '@/assets/images/smg-logo.svg?component';
import AgencySwitcher from '@/components/Agency/AgencySwitcher/AgencySwitcher.vue';
import ModalManager from '@/components/ModalManager/ModalManager.vue';
import { ModalIds, type ModalActionPayload } from '@/components/ModalManager/types';
import type { AccountOwner, AgencyDetails } from '@/types-agency';

import { flatfoxLeadsUrl } from '@/helpers/flatfoxUtils';
import LanguageSwitcher from '@/layouts/Dashboard/LanguageSwitcher.vue';
import { trackHelpCenterLinkClick } from '@/services/tracking/Tracking';
import { useUserStore } from '@/stores/user/useUserStore';

const MENU_OPEN_BODY_CSS_CLASS = 'hg-side-menu-open';
const store = useStore();
const userStore = useUserStore();
const agencyStore = useAgencyStore();
const { activeAgency, isFlatfoxActive, accountOwner } = storeToRefs(agencyStore);
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const { currentLanguage } = defineProps({
  /**
   * The current language of translations.
   */
  currentLanguage: {
    type: String,
    required: true,
  },
});

const isSidebarHidden = ref<boolean>(true);

const customerId = computed<string | undefined>(() => activeAgency.value?.customerId);
const hasCCCGroup = computed(() => userStore.hasCCCGroup);
const supportContact = computed<AccountOwner>(() => ({
  ...accountOwner.value,
  phone: formatSwissPhoneNumber(accountOwner.value.phone),
}));

onMounted(async () => {
  await agencyStore.fetchAndSetUsersForAgency(customerId.value);
});

const openCustomerListingsOnFlatfox = () => {
  window.open(flatfoxLeadsUrl(customerId.value!, currentLanguage as HgSupportedLanguage), '_blank');
};
const closeModal = (modal: ModalIds) => {
  store.dispatch('closeModal', { id: modal } as ModalActionPayload);
};
const openModal = (modal: ModalIds) => {
  store.dispatch('openModal', { id: modal } as ModalActionPayload);
};

const sidebar = ref<HTMLElement | null>(null);
const getSidebarEventNodes = (): Array<HTMLAnchorElement> => {
  return Array.from((sidebar.value as unknown as Element).querySelectorAll('a') || []);
};

const addEventListener = (): void => {
  getSidebarEventNodes().forEach((element) => {
    element.addEventListener('click', hideSidebar);
  });
};

const removeEventListener = (): void => {
  getSidebarEventNodes().forEach((element) => {
    element.removeEventListener('click', hideSidebar);
  });
};

const showSidebar = (): void => {
  toggleSidebar(false);
  addEventListener();
};

const hideSidebar = (): void => {
  toggleSidebar(true);
  removeEventListener();
};

const toggleSidebar = (isHidden: boolean): void => {
  isSidebarHidden.value = isHidden;
  if (isSidebarHidden.value) {
    document.body.classList.remove(MENU_OPEN_BODY_CSS_CLASS);
  } else {
    document.body.classList.add(MENU_OPEN_BODY_CSS_CLASS);
  }
};

const handleAgencySwitch = async (agency: AgencyDetails): Promise<void> => {
  await agencyStore.switchToAgency(agency.customerId);
  await router.push({ name: 'business-listings-manage', query: route.query });
};
</script>

<style lang="scss" module>
@use 'sass:math';

/* stylelint-disable-next-line no-invalid-position-at-import-rule */
@import '@/assets/variables';
/* stylelint-disable-next-line no-invalid-position-at-import-rule */
@import './assets/mixins';
/* stylelint-disable-next-line no-invalid-position-at-import-rule */
@import '@smg-real-estate/domus/style-tokens-mixins.scss';

$header-height: 60px;
$header-height-hg-md: 70px;
$logo-width: 121px;
$sidebar-img-rounded-size: 30px;
$sidebar-img-rounded-size-md: 40px;
$layout-admin-dashboard-indent: $space-100;
$layout-admin-dashboard-indent-lg: $space-150;
$layout-admin-dashboard-col-width: math.div(1008px, 12);
$layout-admin-dashboard-col-width-lg: math.div(1464px, 12);
$layout-admin-dashboard-sidebar-width-base: 280px;
$layout-admin-dashboard-sidebar-width: $layout-admin-dashboard-col-width * 3;
$layout-admin-dashboard-sidebar-width-lg: $layout-admin-dashboard-col-width-lg * 3;
$layout-admin-dashboard-content-max-width: calc(
  #{$layout-admin-dashboard-col-width-lg} * 9 + #{$layout-admin-dashboard-indent-lg}
);

body[class*='hg-side-menu-open'] {
  overflow: hidden;

  @media (min-width: $viewport-md-min) {
    overflow: unset;
  }
}

.backdrop {
  position: fixed;
  inset: 0;
  z-index: -1;
  visibility: hidden;
  background-color: $color-grey-100;
  opacity: 0;
  transition:
    opacity 0.2s ease-in,
    z-index 0.2s linear;

  &.isVisible {
    z-index: 19;
    visibility: visible;
    opacity: 0.35;
  }

  @media (min-width: $viewport-md-min) {
    display: none;
  }
}

.bordered {
  @include logo-bordered;

  & {
    width: $sidebar-img-rounded-size;
    height: $sidebar-img-rounded-size;
    margin-right: $space-75;
  }

  @media (min-width: $viewport-md-min) {
    width: $sidebar-img-rounded-size-md;
    height: $sidebar-img-rounded-size-md;
  }
}

.wrapper {
  position: relative;

  /* This will be used as a fallback when our app encounters a browser that doesn't support the 'dvh' unit */
  min-height: 100vh;

  /*
   The 'dvh' unit accounts for dynamic viewport height,
   which adjusts when the browser's address bar hides/shows on scroll.
   This ensures the layout remains consistent across different viewport scenarios.
   For browser support details, visit: https://caniuse.com/?search=dvh
  */
  min-height: 100dvh;
  background-color: $color-deep-blue-0;

  .reader {
    @include visually-hidden;
  }

  .sidebarToggle {
    display: inline-flex;
    align-items: center;
    padding: $space-50;
  }

  .sidebar {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    width: $layout-admin-dashboard-sidebar-width-base;
    height: 100%;
    color: $color-white;
    background-color: $color-deep-blue-100;
    opacity: 1;
    transition: transform 0.3s ease-in;
    transform: translateX(-100%);

    &.isVisible {
      transform: translateX(0);
    }

    @media (min-width: $viewport-md-min) {
      width: $layout-admin-dashboard-sidebar-width;
      transform: translateX(0);
    }

    @media (min-width: $viewport-lg-min) {
      width: $layout-admin-dashboard-sidebar-width-lg;
    }

    .sidebarSectionTop {
      display: flex;
      align-items: center;
      min-width: 0;
      min-height: $header-height;
      border-bottom: 1px solid $color-grey-70;

      @media (min-width: $viewport-md-min) {
        min-height: $header-height-hg-md;
      }
    }

    .sidebarClose {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      margin-left: $space-50;

      @media (min-width: $viewport-md-min) {
        display: none;
      }
    }

    .sidebarSectionLangs {
      display: block;
      padding-left: $space-100;

      > div > ul > li > a {
        color: $color-white;
        background-color: transparent;
      }

      @media (min-width: $viewport-sm-min) {
        display: none;
      }
    }

    .sidebarSectionMenu {
      display: flex;
      flex: 1;
      flex-direction: column;
      padding: $space-200 $space-100;
      overflow: auto;

      .brandingWrapper {
        display: flex;
        flex: 1;
        flex-direction: column;
        justify-content: flex-end;
      }

      .contactBox {
        display: flex;
        flex-direction: column;
        padding: $space-75 0 $space-100 $space-75;
        margin: auto $space-50 $space-150;
        color: initial;
        background-color: $color-deep-blue-20;
        border-radius: $border-radius-l;

        .headingContainer {
          margin-bottom: $space-50;

          h4 {
            @include heading-4;

            & {
              margin: 0 0 $space-25 0;
              color: $color-black;
            }
          }

          span {
            @include body-3;
          }
        }

        .helpCenterContainer {
          margin-top: $space-50;

          span {
            @include body-3;
          }
        }

        .iconContactContainer {
          display: flex;
          align-items: center;
          justify-content: flex-start;

          .contactIcon {
            @include color-icon($color-sky-80);

            & {
              flex-shrink: 0;
              margin-right: $space-25;
            }
          }

          a {
            @include caption;

            & {
              flex-grow: 1;
              min-width: 0;
              word-break: break-all;
            }
          }
        }
      }

      .SMGLogo {
        display: flex;
        width: $logo-width;
        margin-bottom: $space-50;
        margin-left: $space-200;
      }

      .cccButtons {
        display: grid;
        grid-template-rows: 1fr 1fr;
        grid-template-columns: 1fr;
        row-gap: $space-50;
        place-self: center;
        width: 100%;
        margin-bottom: $space-100;

        & > [class^='HgButton'] {
          &:hover {
            cursor: pointer;
          }

          border-radius: $space-150;
        }

        .ccc-agency-switch-button {
          background-color: $color-poppy-80;
        }

        .ccc-manage-in-flatfox-button {
          background-color: $color-ff-orange-100;
        }
      }
    }
  }
}

.main {
  position: relative;
  display: flex;
  flex-direction: column;
  margin-left: 0;

  @media (min-width: $viewport-md-min) {
    margin-left: $layout-admin-dashboard-sidebar-width;
  }

  @media (min-width: $viewport-lg-min) {
    margin-left: $layout-admin-dashboard-sidebar-width-lg;
  }

  .mainHeader {
    display: flex;
    align-items: center;
    max-width: $layout-admin-dashboard-content-max-width;
    height: $header-height;
    margin-inline: $layout-admin-dashboard-indent;
    color: $color-grey-110;

    @media (min-width: $viewport-md-min) {
      height: $header-height-hg-md;
      margin-inline: $layout-admin-dashboard-indent-lg;
    }
  }

  .sidebarOpen {
    display: block;
    flex-shrink: 0;
    margin-right: $space-50;

    @media (min-width: $viewport-md-min) {
      display: none;
    }
  }

  .navigationWrapper {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    height: 100%;
    margin-left: auto;
  }

  .content {
    flex: 1;
    max-width: $layout-admin-dashboard-content-max-width;
    padding: $space-50 0 $space-300 0;
    margin-inline: $layout-admin-dashboard-indent;

    @media (min-width: $viewport-md-min) {
      padding: $space-50 0 $space-300 0;
      margin-inline: $layout-admin-dashboard-indent-lg;
    }
  }
}
</style>
